import { useEffect, useRef, useState } from 'react'
import { PanelError, isOutcomeUnknown } from '@/console/api/errors'
import { GlobalInboundRollbackRequest, GlobalInboundRollbackResponse, GlobalInboundRollbackResultItem } from '@/console/api/types'
import { requestGlobalInboundRollback } from '@/console/data/globalInboundRollback'
import { ReconfirmStatus, loadReconfirmStatus, saveReconfirmStatus } from '@/console/state/globalRollbackReconfirmStorage'
import {
  hasRolledBack,
  ParsedInboundDocumentNumbers,
  RollbackJudgement,
  buildDryRunRequest,
  buildExecutionRequest,
  interpretRollbackError,
  isJudgementCurrent,
  mayHaveRolledBackDespiteFailure,
  parseInboundDocumentNumbers,
} from './globalRollback'

export const ROLLBACK_REQUEST_KIND = { JUDGING: 'judging', EXECUTING: 'executing' } as const
export type RollbackRequestKind = (typeof ROLLBACK_REQUEST_KIND)[keyof typeof ROLLBACK_REQUEST_KIND]

/** 지금 서버에 나가 있는 호출과 그 문서 수(진행 안내에 쓴다). */
export type RollbackInFlight = { kind: RollbackRequestKind; documentCount: number }

export type DocumentNumberSelection = {
  documentNumbers: string[]
  toggle: (documentNumber: string) => void
  clear: () => void
}

export type GlobalRollbackFlow = {
  /** 입고문서번호 입력란의 원문(한 줄에 하나). */
  documentNumbersText: string
  setDocumentNumbersText: (documentNumbersText: string) => void
  parsed: ParsedInboundDocumentNumbers
  dryRunRequest: GlobalInboundRollbackRequest
  inFlight: RollbackInFlight | null
  requestInFlight: boolean
  error: PanelError | null
  judgement: RollbackJudgement | null
  judgementCurrent: boolean
  executionRequest: GlobalInboundRollbackRequest
  canExecute: boolean
  /** 판정 결과에서 실행 대상으로 고른 문서. */
  selection: DocumentNumberSelection
  executionResults: GlobalInboundRollbackResultItem[] | null
  /** 실행 결과에서 운영자가 "mWMS 화면에서 취소를 확인했다" 고 체크한 문서. 다음 판정의 건너뛰기 목록이 된다. */
  mwmsCancelConfirmation: DocumentNumberSelection
  /** 롤백한 문서가 생긴 뒤 아직 재확정하지 않았는지. 재확정이 성공하면 `markReconfirmed` 로 내린다. */
  reconfirmNeeded: boolean
  /** 실행이 응답 없이 끝나 서버가 아직 처리 중일 수 있는지. 그 실행에 담은 문서를 모두 다시 판정해야 내려간다(§6.5·§6.6). */
  serverMayStillBeProcessing: boolean
  markReconfirmed: () => void
  judge: () => void
  execute: () => void
}

/** 보낸 호출의 결과. 실패하면 `response` 가 null 이고 `cause` 에 원인이 담긴다. */
type RollbackSendResult = { response: GlobalInboundRollbackResponse } | { response: null; cause: unknown }

const useDocumentNumberSelection = (): DocumentNumberSelection => {
  const [documentNumbers, setDocumentNumbers] = useState<string[]>([])
  const toggle = (documentNumber: string): void =>
    setDocumentNumbers((current) =>
      current.includes(documentNumber) ? current.filter((each) => each !== documentNumber) : [...current, documentNumber],
    )

  return { documentNumbers, toggle, clear: () => setDocumentNumbers([]) }
}

/**
 * 호출 하나를 보내고 진행·에러 상태를 관리한다. 실패하면 에러를 해석해 두고 재시도하지 않는다.
 * `sending` 은 리렌더 전에 들어온 두 번째 호출까지 막는다 — 그 호출은 보내지 않고 null 로 끝난다.
 */
const useRollbackRequest = () => {
  const [inFlight, setInFlight] = useState<RollbackInFlight | null>(null)
  const [requestError, setRequestError] = useState<PanelError | null>(null)
  const sending = useRef(false)

  const send = async (request: GlobalInboundRollbackRequest, kind: RollbackRequestKind): Promise<RollbackSendResult | null> => {
    if (sending.current) return null
    sending.current = true
    setInFlight({ kind, documentCount: request.inboundDocumentNumbers.length })
    setRequestError(null)
    try {
      return { response: await requestGlobalInboundRollback(request) }
    } catch (error) {
      setRequestError(interpretRollbackError(error))
      return { response: null, cause: error }
    } finally {
      sending.current = false
      setInFlight(null)
    }
  }

  return { inFlight, requestInFlight: inFlight !== null, error: requestError, send }
}

const mayHaveRolledBack = (result: RollbackSendResult): boolean =>
  result.response ? hasRolledBack(result.response.results) : mayHaveRolledBackDespiteFailure(result.cause)

const isExecutionOutcomeUnknown = (result: RollbackSendResult): boolean => result.response === null && isOutcomeUnknown(result.cause)

const toStatusAfterExecution = (current: ReconfirmStatus, request: GlobalInboundRollbackRequest, result: RollbackSendResult): ReconfirmStatus => ({
  reconfirmNeeded: current.reconfirmNeeded || mayHaveRolledBack(result),
  outcomeUnknownDocumentNumbers: isExecutionOutcomeUnknown(result)
    ? [...new Set([...current.outcomeUnknownDocumentNumbers, ...request.inboundDocumentNumbers])]
    : current.outcomeUnknownDocumentNumbers,
})

/**
 * 실행 결과가 남긴 재확정 숙제. 응답 없이 끝난 실행은 서버가 아직 롤백 중일 수 있어, 그 실행에 담은 문서를
 * 모두 다시 판정할 때까지 재확정을 막는다. 셸이 탭을 벗어나면 패널을 언마운트하므로 저장소에 남긴다.
 */
const useReconfirmStatus = () => {
  const [status, setStatus] = useState<ReconfirmStatus>(loadReconfirmStatus)

  useEffect(() => {
    saveReconfirmStatus(status)
  }, [status])

  const recordExecution = (request: GlobalInboundRollbackRequest, result: RollbackSendResult): void =>
    setStatus((current) => toStatusAfterExecution(current, request, result))

  const recordJudged = (judgedDocumentNumbers: string[]): void =>
    setStatus((current) => ({
      ...current,
      outcomeUnknownDocumentNumbers: current.outcomeUnknownDocumentNumbers.filter((each) => !judgedDocumentNumbers.includes(each)),
    }))

  return {
    reconfirmNeeded: status.reconfirmNeeded,
    serverMayStillBeProcessing: status.outcomeUnknownDocumentNumbers.length > 0,
    recordExecution,
    recordJudged,
    markReconfirmed: () => setStatus((current) => ({ ...current, reconfirmNeeded: false })),
  }
}

/** `send` 가 실패를 결과로 접어 돌려주므로 거절될 일이 없어, 화면 이벤트에서는 기다리지 않고 시작만 한다. */
const fireAndForget = (task: () => Promise<void>) => (): void => {
  task().catch(() => undefined)
}

const EMPTY_EXECUTION_REQUEST: GlobalInboundRollbackRequest = { inboundDocumentNumbers: [], mwmsAlreadyCanceledDocumentNumbers: [], dryRun: false }

type ExecutionReadiness = Pick<GlobalRollbackFlow, 'judgementCurrent' | 'executionRequest' | 'canExecute'>

/** 실행은 지금 입력으로 한 판정에서 고른 문서가 있고, 다른 호출이나 바깥 잠금이 없을 때만 할 수 있다. */
const toExecutionReadiness = (
  judgement: RollbackJudgement | null,
  dryRunRequest: GlobalInboundRollbackRequest,
  selectedDocumentNumbers: string[],
  executionBlocked: boolean,
): ExecutionReadiness => {
  const judgementCurrent = judgement !== null && isJudgementCurrent(judgement, dryRunRequest)
  const executionRequest = judgement ? buildExecutionRequest(judgement, selectedDocumentNumbers) : EMPTY_EXECUTION_REQUEST
  const canExecute = judgementCurrent && executionRequest.inboundDocumentNumbers.length > 0 && !executionBlocked
  return { judgementCurrent, executionRequest, canExecute }
}

/**
 * 판정(dryRun) → 선택 → 실행의 상태. 실행이 끝나면(성공·실패 모두) 판정과 mWMS 취소 확인을 지운다 — 서버 상태가
 * 바뀌었거나 중단 지점을 알 수 없으므로 다시 판정해야 다음 실행을 할 수 있다.
 * `executionLocked` 는 재확정 중처럼 바깥 사정으로 실행을 막아야 할 때 켠다(§6.5).
 */
export const useGlobalRollbackFlow = ({ executionLocked }: { executionLocked: boolean }): GlobalRollbackFlow => {
  const [documentNumbersText, setDocumentNumbersText] = useState('')
  const [judgement, setJudgement] = useState<RollbackJudgement | null>(null)
  const [executionResults, setExecutionResults] = useState<GlobalInboundRollbackResultItem[] | null>(null)
  const selection = useDocumentNumberSelection()
  const mwmsCancelConfirmation = useDocumentNumberSelection()
  const reconfirmStatus = useReconfirmStatus()
  const { inFlight, requestInFlight, error, send } = useRollbackRequest()

  const parsed = parseInboundDocumentNumbers(documentNumbersText)
  const dryRunRequest = buildDryRunRequest(parsed.inboundDocumentNumbers, mwmsCancelConfirmation.documentNumbers)
  const readiness = toExecutionReadiness(judgement, dryRunRequest, selection.documentNumbers, requestInFlight || executionLocked)

  const judge = async (): Promise<void> => {
    setJudgement(null)
    selection.clear()
    const result = await send(dryRunRequest, ROLLBACK_REQUEST_KIND.JUDGING)
    if (!result?.response) return
    setJudgement({ ...dryRunRequest, results: result.response.results })
    reconfirmStatus.recordJudged(dryRunRequest.inboundDocumentNumbers)
  }

  const resetAfterExecution = (request: GlobalInboundRollbackRequest, result: RollbackSendResult): void => {
    setJudgement(null)
    selection.clear()
    mwmsCancelConfirmation.clear()
    setExecutionResults(result.response?.results ?? null)
    reconfirmStatus.recordExecution(request, result)
  }

  // 확인 다이얼로그가 열린 사이에도 입력·진행 상태가 바뀔 수 있어 누른 시점에 다시 확인한다.
  const execute = async (): Promise<void> => {
    if (!readiness.canExecute) return
    const { executionRequest } = readiness
    const result = await send(executionRequest, ROLLBACK_REQUEST_KIND.EXECUTING)
    if (result) resetAfterExecution(executionRequest, result)
  }

  return {
    documentNumbersText,
    setDocumentNumbersText,
    parsed,
    dryRunRequest,
    inFlight,
    requestInFlight,
    error,
    judgement,
    ...readiness,
    selection,
    executionResults,
    mwmsCancelConfirmation,
    reconfirmNeeded: reconfirmStatus.reconfirmNeeded,
    serverMayStillBeProcessing: reconfirmStatus.serverMayStillBeProcessing,
    markReconfirmed: reconfirmStatus.markReconfirmed,
    judge: fireAndForget(judge),
    execute: fireAndForget(execute),
  }
}
